export interface SAIntegration {
  id: string;
  name: string;
  icon: string;
  category: 'database' | 'payments' | 'streaming' | 'email' | 'infrastructure' | 'maps' | 'notifications' | 'social' | 'ai';
  status: 'connected' | 'disconnected' | 'error' | 'pending';
  version: string;
  lastSync: string;
  lastError: string | null;
  configRoute: string;
  logsRoute: string;
}

export type IntegrationFilterValue = 'all' | 'connected' | 'disconnected' | 'error' | 'pending';

export const saIntegrations: SAIntegration[] = [
  {
    id: 'supabase',
    name: 'Supabase',
    icon: 'ri-database-2-line',
    category: 'database',
    status: 'connected',
    version: '2.48.0',
    lastSync: 'Hace 12s',
    lastError: null,
    configRoute: '/sa/settings',
    logsRoute: '/sa/audit-log',
  },
  {
    id: 'stripe',
    name: 'Stripe',
    icon: 'ri-bank-card-line',
    category: 'payments',
    status: 'connected',
    version: '2024-12',
    lastSync: 'Hace 3min',
    lastError: null,
    configRoute: '/sa/payment-processors',
    logsRoute: '/sa/payments',
  },
  {
    id: 'paypal',
    name: 'PayPal',
    icon: 'ri-paypal-line',
    category: 'payments',
    status: 'disconnected',
    version: 'v2',
    lastSync: 'Nunca',
    lastError: 'Falta configurar credenciales',
    configRoute: '/sa/payment-processors',
    logsRoute: '/sa/payments',
  },
  {
    id: 'shopify',
    name: 'Shopify',
    icon: 'ri-shopping-bag-3-line',
    category: 'payments',
    status: 'disconnected',
    version: '—',
    lastSync: 'Nunca',
    lastError: 'Tienda no vinculada',
    configRoute: '/sa/settings',
    logsRoute: '/sa/audit-log',
  },
  {
    id: 'centovacast',
    name: 'CentovaCast',
    icon: 'ri-server-line',
    category: 'streaming',
    status: 'pending',
    version: '3.4.2',
    lastSync: 'Pendiente',
    lastError: 'Esperando aprovisionamiento',
    configRoute: '/sa/settings',
    logsRoute: '/sa/system-health',
  },
  {
    id: 'azuracast',
    name: 'AzuraCast',
    icon: 'ri-cast-line',
    category: 'streaming',
    status: 'connected',
    version: '0.20.1',
    lastSync: 'Hace 1min',
    lastError: null,
    configRoute: '/sa/settings',
    logsRoute: '/sa/system-health',
  },
  {
    id: 'icecast',
    name: 'Icecast',
    icon: 'ri-broadcast-line',
    category: 'streaming',
    status: 'connected',
    version: '2.4.4',
    lastSync: 'Hace 30s',
    lastError: null,
    configRoute: '/sa/settings',
    logsRoute: '/sa/system-health',
  },
  {
    id: 'shoutcast',
    name: 'Shoutcast',
    icon: 'ri-radio-line',
    category: 'streaming',
    status: 'error',
    version: '2.6.1',
    lastSync: 'Hace 45min',
    lastError: 'Timeout de conexión — Puerto 8000',
    configRoute: '/sa/settings',
    logsRoute: '/sa/system-health',
  },
  {
    id: 'smtp',
    name: 'SMTP',
    icon: 'ri-mail-send-line',
    category: 'email',
    status: 'connected',
    version: 'Resend v2',
    lastSync: 'Hace 2min',
    lastError: null,
    configRoute: '/sa/settings',
    logsRoute: '/sa/audit-log',
  },
  {
    id: 'cloudflare',
    name: 'Cloudflare',
    icon: 'ri-cloud-line',
    category: 'infrastructure',
    status: 'connected',
    version: '2025.3',
    lastSync: 'Hace 5min',
    lastError: null,
    configRoute: '/sa/settings',
    logsRoute: '/sa/audit-log',
  },
  {
    id: 'google-maps',
    name: 'Google Maps',
    icon: 'ri-map-pin-line',
    category: 'maps',
    status: 'connected',
    version: '3.58',
    lastSync: 'Hace 10min',
    lastError: null,
    configRoute: '/sa/settings',
    logsRoute: '/sa/audit-log',
  },
  {
    id: 'firebase',
    name: 'Firebase',
    icon: 'ri-fire-line',
    category: 'notifications',
    status: 'disconnected',
    version: '—',
    lastSync: 'Nunca',
    lastError: 'Proyecto no configurado',
    configRoute: '/sa/settings',
    logsRoute: '/sa/notifications',
  },
  {
    id: 'onesignal',
    name: 'OneSignal',
    icon: 'ri-notification-3-line',
    category: 'notifications',
    status: 'connected',
    version: '16.2.0',
    lastSync: 'Hace 30s',
    lastError: null,
    configRoute: '/sa/notifications',
    logsRoute: '/sa/notifications',
  },
  {
    id: 'meta',
    name: 'Meta',
    icon: 'ri-meta-line',
    category: 'social',
    status: 'connected',
    version: 'v21.0',
    lastSync: 'Hace 8min',
    lastError: null,
    configRoute: '/sa/social-status',
    logsRoute: '/sa/social-audit',
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: 'ri-facebook-circle-line',
    category: 'social',
    status: 'connected',
    version: 'v21.0',
    lastSync: 'Hace 7min',
    lastError: null,
    configRoute: '/sa/social-status',
    logsRoute: '/sa/social-audit',
  },
  {
    id: 'youtube',
    name: 'YouTube',
    icon: 'ri-youtube-line',
    category: 'social',
    status: 'connected',
    version: 'v3',
    lastSync: 'Hace 15min',
    lastError: null,
    configRoute: '/sa/social-status',
    logsRoute: '/sa/social-audit',
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    icon: 'ri-tiktok-line',
    category: 'social',
    status: 'pending',
    version: 'v2',
    lastSync: 'Pendiente',
    lastError: 'App en revisión',
    configRoute: '/sa/social-status',
    logsRoute: '/sa/social-audit',
  },
  {
    id: 'openai',
    name: 'OpenAI',
    icon: 'ri-openai-line',
    category: 'ai',
    status: 'connected',
    version: 'GPT-4o',
    lastSync: 'Hace 20s',
    lastError: null,
    configRoute: '/sa/ai-assistant',
    logsRoute: '/sa/ai-assistant',
  },
  {
    id: 'gemini',
    name: 'Gemini',
    icon: 'ri-sparkling-2-line',
    category: 'ai',
    status: 'disconnected',
    version: '—',
    lastSync: 'Nunca',
    lastError: 'API Key no configurada',
    configRoute: '/sa/ai-assistant',
    logsRoute: '/sa/ai-assistant',
  },
];